import React, { useMemo } from 'react';

import { Text } from '@farmersdog/corgi-x';

import { useAvailableRecipes } from '../../../../../graphql/queries/useAvailableRecipes';
import { useCheckVetRecipesAvailability } from '../../../../../hooks/useVetCustomizedPlan/useCheckVetRecipesAvailability';
import { formatCommaSeparatedList } from '../../../../../utils/formatCommaSeparatedList';
import { pluralizeString } from '../../../../../utils/pluralizeString';

import Ellipse from './assets/ellipse.svg';
import styles from './Recipes.module.css';

interface RecipesProps {
  isDiy: boolean;
  freshRecipeNames?: string[];
  diyRecipeDisplayNames?: string[];
  isMobile: boolean;
}

export function Recipes({
  isDiy,
  isMobile,
  diyRecipeDisplayNames,
  freshRecipeNames,
}: RecipesProps) {
  const { data } = useAvailableRecipes();

  const availableFreshRecipesDisplayNames = useMemo(
    () =>
      data?.availableRecipes
        .filter(recipe => freshRecipeNames?.includes(recipe.name))
        .map(recipe => recipe.displayName),
    [data?.availableRecipes, freshRecipeNames]
  );

  const freshVetRecipeNames = useMemo(
    () => freshRecipeNames ?? [],
    [freshRecipeNames]
  );
  const freshAvailableRecipeIdNames = useMemo(() => {
    return isDiy
      ? []
      : (data?.availableRecipes.map(recipe => recipe.name) ?? []);
  }, [isDiy, data?.availableRecipes]);

  const recipes =
    (isDiy ? diyRecipeDisplayNames : availableFreshRecipesDisplayNames) ?? [];

  useCheckVetRecipesAvailability({
    vetRecipeNames: freshVetRecipeNames,
    availableRecipeNames: freshAvailableRecipeIdNames,
  });

  if (isDiy) {
    return isMobile ? (
      <ul className={styles.diyRecipeListStack}>
        {recipes.map(recipe => (
          <li key={recipe}>
            <Text variant="heading-12" bold>
              {recipe}
            </Text>
          </li>
        ))}
      </ul>
    ) : (
      <ul className={styles.diyRecipeList}>
        {recipes.map((recipe, index) => (
          <React.Fragment key={recipe}>
            {index !== 0 && <Ellipse className={styles.ellipse} />}
            <li>
              <Text variant={'heading-16'} bold>
                {recipe}
              </Text>
            </li>
          </React.Fragment>
        ))}
      </ul>
    );
  }

  return (
    <Text variant={isMobile ? 'heading-12' : 'heading-16'} bold>
      {formatCommaSeparatedList(recipes)}{' '}
      {!isDiy && pluralizeString(recipes.length, 'recipe', 'recipes')}
    </Text>
  );
}
